import { formatMoney } from 'shared/src/utils/money/money-utils';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign } from '@fortawesome/pro-light-svg-icons';
import { equalToTwoDecimalPlaces } from 'shared/src/utils/number/number-utils';

type PriceAllocationProgressProps = {
  allocated: number;
  total: number;
  label: string;
  className?: string;
};

export function PriceAllocationProgress({
  allocated,
  total,
  label = '',
  className = ''
}: PriceAllocationProgressProps) {
  const percentage = total > 0 ? Math.min((allocated / total) * 100, 100) : 0;
  const remaining = total - allocated;
  const isComplete = equalToTwoDecimalPlaces(allocated, total);
  const isOverAllocated = !isComplete && allocated > total;

  // Determine progress bar color based on state
  let progressBarClass = 'bg-gray-100'; // Default for 0%
  if (isOverAllocated) {
    progressBarClass = 'bg-red-500';
  } else if (isComplete) {
    progressBarClass = 'bg-linear-to-r from-[#A3E635] to-[#65A30D]'; // Lime gradient for 100% allocation (lighter to darker)
  } else if (percentage > 0) {
    progressBarClass = 'bg-linear-to-b from-[#ca8a04] to-[#fbbf24]'; // Yellow gradient for partial
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="flex flex-col gap-1 rounded-md bg-white p-2">
        <div className="flex items-center gap-1">
          <FontAwesomeIcon className="text-gray-600" icon={faDollarSign} />

          <div className="relative h-4 flex-1 overflow-hidden rounded-lg bg-gray-100 shadow-[0px_1px_2px_0px_inset_rgba(0,0,0,0.1)]">
            <div
              className={`h-full transition-all duration-300 ease-in-out ${progressBarClass} shadow-[0px_1px_2px_0px_inset_rgba(0,0,0,0.15)] ${
                percentage > 0 ? 'rounded-r-lg' : ''
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        </div>

        <div className="flex w-full justify-between text-sm">
          <div className="flex items-center justify-between">
            <span className="mr-1 font-normal text-gray-600">{formatMoney(allocated)} of </span>
            <span className="mr-1 font-semibold">{formatMoney(total)}</span>
            <span className="mr-1 font-light text-gray-600">{label} Allocated</span>
          </div>

          {isOverAllocated && (
            <div className="text-sm text-red-600">
              Over-allocated by {formatMoney(allocated - total)}
            </div>
          )}
          {isComplete && <div className="text-lime-600">Fully Allocated</div>}

          {!isComplete && !isOverAllocated && (
            <div className="flex items-center justify-between">
              <span className="mr-1 text-yellow-700">{formatMoney(remaining)}</span>
              <span className="text-gray-600">Remaining</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
